/**
 * Listing spec plugin resolver (Phase 4).
 *
 * One socket for subcategory-specific specs:
 * derive attributes → validate form → present step-2 UI → title contribution
 * stays in existing composers (`titleComposer` / family runtime).
 *
 * Wave 2 children should register through `specRequirements` + this resolver —
 * not new hard-coded branches in `StepBasedForm` or `sell/page.tsx`.
 */

import {
  getListingFamilySectionLabel,
  listingFamilyHidesBrandLine,
  listingFamilyHidesModelLine,
  listingFamilyHidesSizeLine,
  resolveListingFamilyPluginKind,
} from "@/lib/listing/listingFamilyRegistry";
import {
  getFamilyRuntime,
  readFamilySpec,
  type ListingFamilySpecs,
} from "@/lib/listing/listingFamilyRuntime";
import {
  deriveAttributesFromListingInputs,
  getWaveEnforcementStatus,
  SIZE_LINE_DERIVABLE_SPEC_KEYS,
  validateAttributesForSubcategory,
  type ListingSpecAttributes,
  type ListingSpecWave,
} from "@/lib/listing/specRequirements";

export type ListingSpecPluginKind =
  | "chair"
  | "sofa"
  | "dining_table_chair"
  | "table_chair"
  | "office_table_chairs"
  | "furniture_surface"
  | "ac"
  | "washing_machine"
  | "microwave"
  | "fan"
  | "sewing_machine"
  | "play_station"
  | "gas_cooker"
  | "fridge"
  | "kitchen_cabinet"
  | "registry_size"
  | "none";

export type ListingSpecStepTwoUi =
  | { kind: "none" }
  | { kind: "chair"; sectionLabel: string }
  | { kind: "sofa"; sectionLabel: string }
  | { kind: "dining_table_chair"; sectionLabel: string }
  | { kind: "table_chair"; sectionLabel: string }
  | { kind: "office_table_chairs"; sectionLabel: string }
  | { kind: "furniture_surface"; sectionLabel: string }
  | { kind: "ac"; sectionLabel: string }
  | { kind: "washing_machine"; sectionLabel: string }
  | { kind: "microwave"; sectionLabel: string }
  | { kind: "fan"; sectionLabel: string }
  | { kind: "sewing_machine"; sectionLabel: string }
  | { kind: "play_station"; sectionLabel: string }
  | { kind: "gas_cooker"; sectionLabel: string }
  | { kind: "fridge"; sectionLabel: string }
  | { kind: "kitchen_cabinet"; sectionLabel: string }
  | { kind: "size_line" };

export type ListingSpecFormInput = {
  subcategoryId: string;
  sizeText?: string;
  familySpecs?: ListingFamilySpecs;
  activeSubcategoryIds?: Iterable<string>;
  enabledWaves?: readonly ListingSpecWave[];
};

/** Which plugin owns this subcategory (UI + attribute build). */
export function resolveListingSpecPluginKind(
  subcategoryId: string | null | undefined,
): ListingSpecPluginKind {
  if (!subcategoryId) return "none";
  // Registered families resolve from listingFamilyRegistry first.
  const registeredKind = resolveListingFamilyPluginKind(subcategoryId);
  if (registeredKind) return registeredKind;
  return "registry_size";
}

/** Step 2 presentation family — components map `kind` to field UIs. */
export function resolveListingSpecStepTwoUi(
  subcategoryId: string | null | undefined,
): ListingSpecStepTwoUi {
  if (!subcategoryId) return { kind: "none" };
  const registeredKind = resolveListingFamilyPluginKind(subcategoryId);
  const registeredLabel = getListingFamilySectionLabel(subcategoryId);
  // Registry families: one Step 2 kind shape — no per-family name checks here.
  if (registeredKind && registeredLabel) {
    return { kind: registeredKind, sectionLabel: registeredLabel };
  }
  return { kind: "size_line" };
}

/**
 * Seller-facing form validation for step 2 / submit.
 * Registry families: always when subcategory matches.
 * Registry size line: only when wave enforcement is active.
 */
export function getListingSpecFormValidationMessage(
  input: ListingSpecFormInput,
): string | undefined {
  const { subcategoryId } = input;
  if (!subcategoryId) return undefined;

  const familyRuntime = getFamilyRuntime(subcategoryId);
  if (familyRuntime) {
    return familyRuntime.validateForm(
      readFamilySpec(input.familySpecs, familyRuntime.familyId),
      { subcategoryId },
    );
  }

  if (!input.activeSubcategoryIds) return undefined;
  const wave = getWaveEnforcementStatus(
    subcategoryId,
    input.activeSubcategoryIds,
    input.enabledWaves,
  );
  if (!wave.shouldEnforce) return undefined;

  const attrs = deriveAttributesFromListingInputs({
    subcategoryId,
    sizeText: input.sizeText ?? "",
  });
  const result = validateAttributesForSubcategory(subcategoryId, attrs, {
    enforceRequired: true,
    rejectUnknownKeys: false,
    enforceRequiredKeys: SIZE_LINE_DERIVABLE_SPEC_KEYS,
  });
  return result.issues[0]?.message;
}

/** Build `items.attributes` for create / submit from form inputs. */
export function buildListingAttributesForSubmit(input: {
  subcategoryId: string;
  sizeText?: string;
  familySpecs?: ListingFamilySpecs;
}): ListingSpecAttributes {
  const familyRuntime = getFamilyRuntime(input.subcategoryId);
  if (familyRuntime) {
    return familyRuntime.buildAttrs(
      readFamilySpec(input.familySpecs, familyRuntime.familyId),
      { subcategoryId: input.subcategoryId },
    );
  }

  return deriveAttributesFromListingInputs({
    subcategoryId: input.subcategoryId,
    sizeText: input.sizeText ?? "",
  });
}

/**
 * Server-side attribute validation when wave enforcement is on.
 * Returns first user-facing message or undefined when OK / not enforced.
 */
export function getListingSpecAttributesEnforcementError(input: {
  subcategoryId: string;
  attributes: ListingSpecAttributes;
  activeSubcategoryIds: Iterable<string>;
  enabledWaves?: readonly ListingSpecWave[];
}): string | undefined {
  const wave = getWaveEnforcementStatus(
    input.subcategoryId,
    input.activeSubcategoryIds,
    input.enabledWaves,
  );
  if (!wave.shouldEnforce) return undefined;
  const kind = resolveListingSpecPluginKind(input.subcategoryId);
  const result = validateAttributesForSubcategory(
    input.subcategoryId,
    input.attributes,
    {
      enforceRequired: true,
      // Size-line path: only enforce size-derivable required keys until appliance
      // Phase 2 UI owns Step 2. Plugin-owned families enforce the full set.
      ...(kind === "registry_size" || kind === "none"
        ? { enforceRequiredKeys: SIZE_LINE_DERIVABLE_SPEC_KEYS }
        : {}),
    },
  );
  if (result.isValid) return undefined;
  return (
    result.issues[0]?.message ??
    "Please complete the required size or specification details for this item."
  );
}

/** Whether step 2 should hide the generic size line (plugin owns the slot). */
export function listingSpecPluginHidesSizeLine(
  subcategoryId: string | null | undefined,
): boolean {
  return listingFamilyHidesSizeLine(subcategoryId);
}

/** Whether sell should hide the category brand picker for this subcategory. */
export function listingSpecPluginHidesBrandLine(
  subcategoryId: string | null | undefined,
): boolean {
  return listingFamilyHidesBrandLine(subcategoryId);
}

/** Whether sell should hide the generic model field for this subcategory. */
export function listingSpecPluginHidesModelLine(
  subcategoryId: string | null | undefined,
): boolean {
  return listingFamilyHidesModelLine(subcategoryId);
}
